import React, { useMemo, useState } from 'react';
import { CheckCircle2, Download, FileSpreadsheet, Images, Loader2, Upload } from 'lucide-react';
import { toast } from 'react-toastify';
import { readSheetFile } from '../../lib/sheetReader';
import {
  analyzeBulkRows,
  bulkResultsCsv,
  bulkTemplateCsv,
  financeDedupeKey,
  handDedupeKey,
  mapSheet,
  matchRowPhoto,
  photoFileKey,
  type BulkCells,
  type BulkRow,
} from '../../lib/bulkMigration';
import { buildMigratedFinanceData, buildMigratedHandData, dateLabel } from '../../lib/migrationRecords';
import { readFinanceRecords, readHandLoanRecords, saveImportedLoanRecord } from '../../lib/workspaceStore';
import { adminEntryIso } from '../../lib/workspaceAsOf';
import { fileToCompressedDataUrl } from '../../lib/imageResize';
import { downloadFile } from '../../lib/exportReports';

/** Each loan is one API request; stay well inside the server's per-IP request limit. */
const MAX_ROWS_PER_IMPORT = 500;
/** Bulk photos are kept small: hundreds of them live in every device's local cache. */
const BULK_PHOTO_BYTES = 20 * 1024;

const inr = (n: number) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

type ImportResult = { rowNumber: number; name: string; status: string; message: string };

function existingLoanKeys(): Set<string> {
  const keys = new Set<string>();
  for (const r of readFinanceRecords()) keys.add(financeDedupeKey(r.data as Record<string, unknown>));
  for (const r of readHandLoanRecords()) keys.add(handDedupeKey(r.data as Record<string, unknown>));
  return keys;
}

const statusStyle: Record<BulkRow['status'], string> = {
  ready: 'text-emerald-700 dark:text-emerald-300',
  error: 'text-rose-700 dark:text-rose-300',
  duplicate: 'text-amber-700 dark:text-amber-300',
  skipped: 'text-zinc-500 dark:text-zinc-400',
};

export function BulkMigrateImport({
  canWrite,
  cutoverDefault,
  onDone,
}: {
  canWrite: boolean;
  cutoverDefault: string;
  onDone: () => void;
}) {
  const [cutover, setCutover] = useState(cutoverDefault);
  const [fileName, setFileName] = useState('');
  const [sheet, setSheet] = useState<{ cells: BulkCells[]; rowNumbers: number[] } | null>(null);
  const [photos, setPhotos] = useState<Map<string, File>>(new Map());
  const [reading, setReading] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [results, setResults] = useState<ImportResult[] | null>(null);
  const [filter, setFilter] = useState<'all' | 'problems'>('all');

  const rows = useMemo(
    () => (sheet ? analyzeBulkRows(sheet.cells, sheet.rowNumbers, { defaultCutoverYmd: cutover, existingKeys: existingLoanKeys() }) : []),
    [sheet, cutover]
  );
  const ready = rows.filter((r) => r.status === 'ready');
  const count = (s: BulkRow['status']) => rows.filter((r) => r.status === s).length;
  const photosMatched = ready.filter((r) => matchRowPhoto(r, photos)).length;
  const overdueLoans = ready.filter((r) => r.overdueCount > 0);
  const shown = filter === 'all' ? rows : rows.filter((r) => r.status === 'error' || r.status === 'duplicate');
  const importing = progress !== null && results === null;

  const downloadTemplate = () => {
    void downloadFile(new Blob([bulkTemplateCsv()], { type: 'text/csv;charset=utf-8;' }), 'migrate-loans-template.csv');
  };

  const onSheet = async (file: File | undefined) => {
    if (!file) return;
    setReading(true);
    setResults(null);
    setProgress(null);
    try {
      const mapped = mapSheet(await readSheetFile(file));
      if (mapped.missing.length) {
        toast.error(`These columns are missing: ${mapped.missing.join(', ')}. Start from the template.`);
        setSheet(null);
        return;
      }
      if (!mapped.cells.length) {
        toast.error('The sheet has headings but no loan rows.');
        setSheet(null);
        return;
      }
      setFileName(file.name);
      setSheet(mapped);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not read this file.');
      setSheet(null);
    } finally {
      setReading(false);
    }
  };

  const onPhotos = (files: FileList | null) => {
    const map = new Map<string, File>();
    for (const f of Array.from(files || [])) {
      if (f.type.startsWith('image/')) map.set(photoFileKey(f.name), f);
    }
    setPhotos(map);
  };

  const runImport = async () => {
    if (!canWrite || !ready.length) return;
    if (ready.length > MAX_ROWS_PER_IMPORT) {
      toast.error(`Import at most ${MAX_ROWS_PER_IMPORT} loans at a time. Split the sheet into smaller files.`);
      return;
    }
    if (!window.confirm(`Import ${ready.length} loan(s) with cutover ${dateLabel(cutover)} where the sheet has no cutover date?`)) return;
    const out: ImportResult[] = rows
      .filter((r) => r.status !== 'ready')
      .map((r) => ({ rowNumber: r.rowNumber, name: r.name, status: r.status === 'error' ? 'Not imported' : 'Skipped', message: r.message }));
    setProgress({ done: 0, total: ready.length });
    let done = 0;
    for (const row of ready) {
      try {
        const photoFile = matchRowPhoto(row, photos);
        let photo = '';
        if (photoFile) {
          try {
            photo = await fileToCompressedDataUrl(photoFile, BULK_PHOTO_BYTES);
          } catch {
            photo = '';
          }
        }
        const built =
          row.kind === 'finance' && row.financeInput
            ? buildMigratedFinanceData({ ...row.financeInput, applicantPhoto: photo })
            : row.handInput
              ? buildMigratedHandData({ ...row.handInput, applicantPhoto: photo })
              : null;
        if (!built || !built.ok) throw new Error(built && !built.ok ? built.error : 'Row could not be prepared.');
        await saveImportedLoanRecord(row.kind === 'finance' ? 'finance' : 'handloan', {
          id: crypto.randomUUID(),
          createdAt: adminEntryIso(row.cutoverYmd),
          updatedAt: new Date().toISOString(),
          source: 'local',
          data: built.data,
        });
        out.push({
          rowNumber: row.rowNumber,
          name: row.name,
          status: 'Imported',
          message: `${row.message}${photoFile ? (photo ? ' · photo added' : ' · photo could not be read') : ''}`,
        });
      } catch (e) {
        out.push({ rowNumber: row.rowNumber, name: row.name, status: 'Failed', message: e instanceof Error ? e.message : 'Server did not accept this loan.' });
      }
      done += 1;
      setProgress({ done, total: ready.length });
    }
    out.sort((a, b) => a.rowNumber - b.rowNumber);
    setResults(out);
    const ok = out.filter((r) => r.status === 'Imported').length;
    const failed = out.filter((r) => r.status === 'Failed').length;
    if (failed) toast.error(`${ok} imported, ${failed} failed. Download the results to see why.`);
    else toast.success(`${ok} loan(s) imported.`);
  };

  const downloadResults = () => {
    if (!results) return;
    void downloadFile(new Blob([bulkResultsCsv(results)], { type: 'text/csv;charset=utf-8;' }), 'migrate-loans-results.csv');
  };

  if (results) {
    const ok = results.filter((r) => r.status === 'Imported').length;
    const failed = results.filter((r) => r.status === 'Failed');
    return (
      <div className="app-card app-card-inner space-y-4">
        <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:border-emerald-800/50 dark:bg-emerald-950/30 dark:text-emerald-100">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {ok} loan(s) imported{failed.length ? `, ${failed.length} failed` : ''}. Rows with errors or duplicates were not imported.
            Fix them in the sheet and upload it again — rows already imported are recognised and skipped.
          </span>
        </div>
        {failed.length ? (
          <ul className="max-h-40 space-y-1 overflow-y-auto text-xs text-rose-700 dark:text-rose-300">
            {failed.map((r) => (
              <li key={r.rowNumber}>
                Row {r.rowNumber} · {r.name}: {r.message}
              </li>
            ))}
          </ul>
        ) : null}
        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" className="app-btn-secondary inline-flex items-center gap-2" onClick={downloadResults}>
            <Download className="h-4 w-4" /> Download results
          </button>
          <button type="button" className="app-btn-primary" onClick={onDone}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-card app-card-inner space-y-5">
      <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-300">
        <FileSpreadsheet className="h-4 w-4" />
        <div>
          <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Import many loans from Excel</h2>
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            Finance and hand loans in one sheet. Each row is checked with the same rules as the single-loan form before anything is saved.
          </p>
        </div>
      </div>

      <ol className="list-decimal space-y-1 pl-5 text-xs text-zinc-600 dark:text-zinc-300">
        <li>Download the template and fill one row per loan (delete or keep the EXAMPLE rows — they are never imported).</li>
        <li>
          For each loan enter the outstanding principal, the EMIs still to collect <b>including</b> overdue ones, how many EMIs are overdue,
          and the next due date. Overdue days and penalty are worked out from these.
        </li>
        <li>Dates can be written as 15-10-2026, 15/10/2026, 15-Oct-2026 or 2026-10-15 (day first).</li>
        <li>Save as .xlsx or .csv and upload it. Photos are optional.</li>
      </ol>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div>
          <label className="app-label">1. Template</label>
          <button type="button" className="app-btn-secondary inline-flex w-full items-center justify-center gap-2" onClick={downloadTemplate}>
            <Download className="h-4 w-4" /> Download template
          </button>
        </div>
        <div>
          <label className="app-label">Cutover date (used when a row has none)</label>
          <input type="date" className="app-field" value={cutover} onChange={(e) => setCutover(e.target.value)} disabled={importing} />
        </div>
        <div>
          <label className="app-label">2. Filled sheet (.xlsx or .csv)</label>
          <label className="app-btn-secondary inline-flex w-full cursor-pointer items-center justify-center gap-2">
            {reading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {fileName || 'Choose file'}
            <input
              type="file"
              accept=".xlsx,.csv"
              className="hidden"
              disabled={importing}
              onChange={(e) => {
                void onSheet(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </label>
        </div>
        <div className="md:col-span-3">
          <label className="app-label">3. Photos (optional)</label>
          <label className="app-btn-secondary inline-flex cursor-pointer items-center gap-2">
            <Images className="h-4 w-4" />
            {photos.size ? `${photos.size} photo(s) selected` : 'Choose photos'}
            <input type="file" accept="image/*" multiple className="hidden" disabled={importing} onChange={(e) => onPhotos(e.target.files)} />
          </label>
          <p className="mt-1 text-[11px] text-zinc-500 dark:text-zinc-400">
            A photo is matched to a loan by the "Photo file name" column, or when the file is named after the HA number or the mobile number
            (for example 9876543210.jpg). Loans without a photo can get one later from the customer profile.
          </p>
        </div>
      </div>

      {sheet ? (
        <>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-semibold text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
              Ready {ready.length}
            </span>
            <span className="rounded-full bg-rose-50 px-2.5 py-1 font-semibold text-rose-800 dark:bg-rose-950/40 dark:text-rose-200">Errors {count('error')}</span>
            <span className="rounded-full bg-amber-50 px-2.5 py-1 font-semibold text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
              Duplicates {count('duplicate')}
            </span>
            {count('skipped') ? (
              <span className="rounded-full bg-zinc-100 px-2.5 py-1 font-semibold text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">Example rows {count('skipped')}</span>
            ) : null}
            <span className="rounded-full bg-zinc-100 px-2.5 py-1 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
              Outstanding {inr(ready.reduce((s, r) => s + r.outstanding, 0))}
            </span>
            <span className="rounded-full bg-zinc-100 px-2.5 py-1 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
              {overdueLoans.length} overdue loan(s) · {inr(overdueLoans.reduce((s, r) => s + r.overdueAmount, 0))}
            </span>
            <span className="rounded-full bg-zinc-100 px-2.5 py-1 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
              Photos matched {photosMatched}/{ready.length}
            </span>
          </div>

          <div className="flex items-center justify-between gap-2">
            <select className="app-field max-w-[220px]" value={filter} onChange={(e) => setFilter(e.target.value as 'all' | 'problems')}>
              <option value="all">All rows ({rows.length})</option>
              <option value="problems">Only errors and duplicates ({count('error') + count('duplicate')})</option>
            </select>
          </div>

          <div className="app-table-wrap max-h-[420px] overflow-auto">
            <table className="app-table min-w-full text-xs">
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Type</th>
                  <th>Customer</th>
                  <th>Mobile</th>
                  <th>Outstanding</th>
                  <th>EMI</th>
                  <th>Next due</th>
                  <th>Overdue at cutover</th>
                  <th>Photo</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.rowNumber}>
                    <td className="tabular-nums">{r.rowNumber}</td>
                    <td>{r.kind === 'finance' ? 'Finance' : r.kind === 'handloan' ? 'Hand' : '—'}</td>
                    <td>{r.name || '—'}</td>
                    <td>{r.mobile || '—'}</td>
                    <td className="tabular-nums">{r.outstanding ? inr(r.outstanding) : '—'}</td>
                    <td className="tabular-nums">{r.emi ? inr(r.emi) : '—'}</td>
                    <td>{r.nextDueYmd ? dateLabel(r.nextDueYmd) : '—'}</td>
                    <td className="tabular-nums">
                      {r.overdueCount ? `${r.overdueCount} · ${inr(r.overdueAmount)} · ${r.oldestOverdueDays} days` : r.status === 'ready' ? 'None' : '—'}
                    </td>
                    <td>{matchRowPhoto(r, photos) ? 'Yes' : '—'}</td>
                    <td className={statusStyle[r.status]}>{r.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end">
            {progress ? (
              <span className="text-xs text-zinc-600 dark:text-zinc-300">
                Importing {progress.done} / {progress.total}… keep this page open.
              </span>
            ) : null}
            <button type="button" className="app-btn-primary inline-flex items-center justify-center gap-2" disabled={!canWrite || !ready.length || importing} onClick={() => void runImport()}>
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Import {ready.length} loan(s)
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}
